import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import publicationsService from '../services/publicationsService';
import {Publication} from '../types/Publication';
import {DataTable, DataTableFilterMeta} from 'primereact/datatable';
import {Column} from 'primereact/column';
import {Button} from 'primereact/button';
import {useNavigate} from "react-router-dom";
import {handleError} from "../../../common/utils/errorHandling";
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton} from "../../../layout/admin";
import {FilterMatchMode} from "primereact/api";
import {useToast} from "../../../common/contexts/ToastContext";
import {useAdminConfirmDialog} from "../../../common/hooks/useAdminConfirmDialog";

const PublicationsList: React.FC = () => {
  const {t, i18n} = useTranslation();
  const {showError, showSuccess} = useToast();
  const {showConfirmDialog} = useAdminConfirmDialog();
  const [publications, setPublications] = useState<Publication[]>([]);
  const navigate = useNavigate();
  const [filters, setFilters] = useState<DataTableFilterMeta>({
    name: {value: null, matchMode: FilterMatchMode.CONTAINS},
    title: {value: null, matchMode: FilterMatchMode.CONTAINS}
  });

  useEffect(() => {
    fetchPublications();
  }, []);

  const fetchPublications = async (): Promise<void> => {
    const publicationsData = await publicationsService.getAllPublications();
    setPublications(publicationsData);
  };

  const handleDelete = async (publication: Publication): Promise<void> => {
    showConfirmDialog({
      message: t('admin.common.messages.confirmDeleteWithName', {name: publication.name}),
      accept: async () => {
        await handleError(
          async () => {
            await publicationsService.deletePublication(publication.id);
            await fetchPublications();
          },
          t('admin.publications.errors.deleting')
        );
      }
    });
  };

  const handleEdit = (publication: Publication) => {
    navigate(`${publication.id}/edit`);
  };

  const handleCreateNew = () => {
    navigate('new');
  };

  const truncateDescription = (description?: string) => {
    if (!description) return '';
    return description.length > 100 ? description.substring(0, 100) + '...' : description;
  };

  return (
    <>
      <AdminFormHeader
        title={t('admin.publications.title.list')}
        subtitle={t('admin.publications.subtitle.list')}
      />

      <AdminButton
        onClick={handleCreateNew}
        variant="black"
        label={t('admin.publications.actions.createNew')}
      />

      <DataTable
        value={publications}
        stripedRows
        paginator
        rows={10}
        filterDisplay="row"
        filters={filters}
        columnResizeMode="fit"
        resizableColumns
        showGridlines
      >
        <Column
          sortField="name"
          header={t('admin.common.columns.name')}
          sortable
          filter
          filterField="name"
          filterPlaceholder={t('admin.common.filters.searchByName')}
          showFilterMenu={false}
          showClearButton={false}
          body={(publication: Publication) => (
            <strong>{publication.name}</strong>
          )}/>
        <Column
          sortField="title"
          header={t('admin.publications.columns.title')}
          sortable
          filter
          filterField="title"
          filterPlaceholder={t('admin.publications.filters.searchByTitle')}
          showFilterMenu={false}
          showClearButton={false}
          body={(publication: Publication) => (
            <span>{publication.title}</span>
          )}/>
        <Column
          sortField="publicationDate"
          header={t('admin.publications.columns.publicationDate')}
          sortable
          body={(publication: Publication) => (
            <span>{publication.publicationDate ? publication.publicationDate.substring(0, 4) : '-'}</span>
          )}/>
        <Column header={t('admin.common.columns.actions')} body={(publication: Publication) => (
          <div>
            <Button
              onClick={() => handleEdit(publication)}
              icon="fa fa-edit"
              severity="info"
              size="small"
              outlined
              tooltip={t('admin.common.actions.edit')}
              tooltipOptions={{position: 'top'}}
            />
            <Button
              onClick={() => handleDelete(publication)}
              icon="fa fa-trash"
              severity="danger"
              size="small"
              outlined
              className="ml-2"
              tooltip={t('admin.common.actions.delete')}
              tooltipOptions={{position: 'top'}}
            />
          </div>
        )}/>
      </DataTable>
    </>
  );
};

export default PublicationsList;

